import { notification } from "ant-design-vue";
import { reactive, defineAsyncComponent } from "vue";
import i18n from "../../../../i18n";
import store from "../../../../store";
import {
    onClickActiveAll,
    onClickArchiveAll,
    onClickCreate,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import {
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit, createRowActionOpenUrl,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { openModalForm as openModalFormConfirmApprove } from "./modal/confirm-approve.logic";
import { createConditionAction } from "./fee.condition";
import { callActiveAll, callArchiveAll, callDeleteAll, callSearch } from "./fee.logic";
import { createColumns } from "./fee.section";
import { has } from "../../../../utils/menu-context";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/feeStatement";

export const callSaveAll = ({ forms }) => store.dispatch(`${actionPrefix}/saveAll`, { data: forms });

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...where, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const createWhereFor = ({ props }) => {
    const { menuContext, visibleApprove } = props;
    const userInfo = store.state.resource.auth.info?.user;
    const whereGroup = [];
    if (!has({ menuContext, key: "forApprove" })) {
        if (!userInfo.refOfficerLv) {
            whereGroup.push([
                { openBucket: true, name: "refRequest.createBy", type: "String", operator: "=", value: userInfo.username },
                { logic: "OR", name: "requestNumber1", type: "String", operator: "=", value: userInfo.idCard },
                { logic: "OR", name: "refUser.username", type: "String", operator: "=", value: userInfo.username },
                { closeBucket: true, logic: "OR", name: "refUserCompany.username", type: "String", operator: "=", value: userInfo.username },
            ]);
        }
        if (userInfo.refOfficerLv?.key?.startsWith("OFFICE_")) {
            whereGroup.push([
                { name: "refRequest.refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment.id },
            ])
        }
    }
    return [
        { name: "refStatus.key", type: "String", operator: "in", value: visibleApprove ? ["waitApprove"] : ["approve", "cancel"] },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix, visibleApprove } = props;
    state.columns = createColumns({ props })
    state.manages = [
        createManageSelect({ menuContext }),
        {
            icon: () => <CIcon file="check.svg"></CIcon>,
            text: () => i18n.global.t("button.approve"),
            force: true,
            visible: () => visibleApprove,
            rowSelection: true,
            buttonProp: { class: "btn-success" },
            onClick: ({ rowSelection }) => {
                openModalFormConfirmApprove({
                    selectedRows: rowSelection.selectedRows,
                    onOk: record => {
                        setIsLoading(true);
                        const forms = rowSelection.selectedRows.map(d => ({ ...d, refStatus: record.refStatus }));
                        callSaveAll({ forms }).then(() => {
                            notification["success"]({ message: i18n.global.t("views.request.approve.success") });
                            onSearch({ props, page: state.page });
                        }).finally(() => setIsLoading(false));
                    }
                });
            },
            getCheckboxProps: ({ record }) => ({ disabled: record.refStatus?.key !== "waitApprove" })
        },
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="printer.svg"></CIcon> },
            text: () => i18n.global.t("button.viewDocument"),
            callReport: ({ record }) => callReportReceipt({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            visible: ({ record }) => record.refStatus.key == 'approve',
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/fee/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`
        })
    ];
};

export const state = reactive({
    page: createPage({
        options: {
            select: ["**", "refRequest.refRequestDoc.*"],
            sort: [/* { name: "refStatus.seq", type: "ASC" } ,*/ { name: "updateDate", type: "DESC" }]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
